import { defineComponent as _defineComponent } from 'vue'
import Vue, { ref, onMounted, computed, onBeforeUnmount, getCurrentInstance } from "vue";
import { cloneDeep, debounce } from "lodash";
import deepEqual from "deep-equal";
import { useI18n } from "vue-i18n-bridge";
import ThemeTextField from "@/components/molecules/ThemeTextField.vue";
import { showNotifyDialog, showExampleDialog, showOkCancelDialog } from "@/lib/dialog";
import type { Detail } from "@/lib/models/theme";
import { usePageEditStore } from "@/stores/pageEdit";
import { useThemeStore } from "@/stores/theme";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import { ThemeAnalytics, ThemeGenerateAnalytics, ThemeSaveTrigger } from "@/lib/utils/analytics/theme";
import { isMobile } from "@/constants/breakpoints";
import { THEME_FIELD_NAMES, THEME_GENERATE_APPLY_EVENT } from "@/constants/themeGenerate";
import {
  consumePendingThemeGenerateApply,
  type ThemeGenerateFields,
} from "@/lib/utils/themeGenerateApply";
import { scrollToThemeField } from "@/lib/utils/themeMatchingIssues";
import IconInfo from "~icons/figma/Info";


export default /*#__PURE__*/_defineComponent({
  __name: 'ThemeEditor',
  props: {
    novelId: { type: String, required: true }
  },
  setup(__props: any) {

const props = __props as {
  novelId: string;
};



const { t } = useI18n();

const theme = ref<Record<string, string>>({});
const preTheme = ref<Record<string, string>>({});
const unbindShortcutKeys = ref<null | (() => void)>(null);
const isMobileScreen = ref(isMobile());
/** フォーカス/編集後に文字数超過しているフィールド（true = 保存不可） */
const fieldLengthErrors = ref<Record<string, boolean>>({});

const onResize = debounce(() => {
  isMobileScreen.value = isMobile();
}, 150);

const pageEditStore = usePageEditStore();
const themeStore = useThemeStore();
const { fetchTheme, updateTheme } = themeStore;

// ////////////////////////////////////////
// Analytics
// ////////////////////////////////////////
const instance = getCurrentInstance();
const gtm = instance?.proxy.$gtm;
const analytics = gtm ? new ThemeAnalytics(gtm) : null;
const generateAnalytics = gtm ? new ThemeGenerateAnalytics(gtm) : null;

async function applyGeneratedTheme(event: Event) {
  const custom = event as CustomEvent<{ fields?: ThemeGenerateFields }>;
  const fields = custom.detail?.fields ?? {};
  const names = THEME_FIELD_NAMES.filter((name) => typeof fields[name] === "string" && fields[name] !== "");
  if (names.length === 0) {
    Vue.notify({
      title: t("themeGenerate.apply.empty"),
      type: "warn",
    });
    return;
  }

  const overwritten = names.some((name) => (theme.value[name] ?? "").trim().length > 0);
  if (overwritten) {
    const confirmed = await showOkCancelDialog({
      title: t("themeGenerate.apply.overwriteTitle"),
      content: t("themeGenerate.apply.overwriteMessage"),
      okButton: t("themeGenerate.apply.overwriteOk"),
      cancelButton: t("themeGenerate.apply.overwriteCancel"),
    });
    if (!confirmed) return;
  }

  const next = { ...theme.value };
  names.forEach((name) => {
    next[name] = fields[name] as string;
  });
  theme.value = next;

  scrollToThemeField(names[0]);

  Vue.notify({
    title: t("themeGenerate.apply.applied"),
    type: "success",
  });

  generateAnalytics?.trackApply({
    novelId: props.novelId,
    fieldCount: names.length,
    overwritten,
  });
}

onMounted(async () => {
  window.addEventListener("resize", onResize);
  window.addEventListener(THEME_GENERATE_APPLY_EVENT, applyGeneratedTheme);

  await fetchTheme(props.novelId);
  if (themeStore.theme.detail) {
    const detail: Record<string, string> = {};
    themeStore.theme.detail.forEach((item) => {
      detail[item.name] = item.value;
    });
    theme.value = detail;
    preTheme.value = cloneDeep(detail);
  }

  const pendingFields = consumePendingThemeGenerateApply(props.novelId);
  if (pendingFields) {
    await applyGeneratedTheme(
      new CustomEvent(THEME_GENERATE_APPLY_EVENT, { detail: { fields: pendingFields } })
    );
  }

  // 保存のショートカットキーの有効化と解除関数の取得
  unbindShortcutKeys.value = handleShortcutKeys([
    { trigger: TriggerKey.Meta, keys: ["s"], callback: () => submit("shortcut") },
    { trigger: TriggerKey.Ctrl, keys: ["s"], callback: () => submit("shortcut") },
  ]);
});

onBeforeUnmount(() => {
  // コンポーネントが破棄される前に保存のショートカットキーを解除
  if (unbindShortcutKeys.value) unbindShortcutKeys.value();
  window.removeEventListener("resize", onResize);
  window.removeEventListener(THEME_GENERATE_APPLY_EVENT, applyGeneratedTheme);
  // debounce 内に予約された setTimeout が破棄後に発火し、
  // 破棄済みコンポーネントの ref を更新するのを防ぐ
  onResize.cancel();
});

const isValidWordLength = computed(() => {
  // テーマの執筆予定文字数は未入力を許容する
  if (!theme.value["word-length"]) {
    return true;
  }

  // 半角数字のみで構成されているかチェックする正規表現
  const isNumeric = /^\d+$/.test(theme.value["word-length"]);
  // 半角数字のみである場合にtrueを返し、それ以外はfalseを返す
  return isNumeric;
});

const changed = computed(() => {
  const changed = !deepEqual(preTheme.value, theme.value);
  pageEditStore.setModified(changed);
  return changed;
});

const hasLengthError = computed(() => Object.values(fieldLengthErrors.value).some(Boolean));

const canSave = computed(() => changed.value && !hasLengthError.value);

function onFieldValidationChange(name: string, hasError: boolean) {
  if (fieldLengthErrors.value[name] === hasError) return;
  fieldLengthErrors.value = { ...fieldLengthErrors.value, [name]: hasError };
}

async function submit(trigger: ThemeSaveTrigger = "button") {
  if (!canSave.value) return;

  if (!isValidWordLength.value) {
    await showNotifyDialog({
      title: t("themePage.inputErrorTitle"),
      content: t("themePage.inputErrorMessage"),
    });
    return;
  }

  const detail = Object.keys(theme.value).map((key) => ({
    name: key,
    value: theme.value[key],
  }));

  const input = {
    novelId: props.novelId,
    detail: filterWordLengthDetails(detail),
  };

  // GA: 保存（updateTheme より先に飛ばし、ボタン押下〜送信のラグと
  // 直後の画面遷移によるリクエスト欠落を最小化する。失敗時の補正は別途）
  analytics?.trackSave({
    novelId: props.novelId,
    theme: theme.value,
    trigger,
  });

  await updateTheme(input);

  Vue.notify({
    title: t("themePage.saveSuccessMessage"),
    type: "success",
  });

  pageEditStore.setModified(false);
  preTheme.value = cloneDeep(theme.value);
}

function filterWordLengthDetails(details: Detail[]) {
  const index = details.findIndex((detail) => detail.name === "word-length");
  if (index === -1) return details; // word-length属性が存在しない場合、元の配列を返す

  const wordLengthDetail = details[index];
  if (wordLengthDetail.value === "" || wordLengthDetail.value === undefined) {
    // word-length属性が空文字またはundefinedの場合、その要素を除外
    return details.filter((_, idx) => idx !== index);
  }

  return details;
}

function formatWordLength() {
  const parsedValue = parseInt(theme.value["word-length"], 10);
  if (!Number.isNaN(parsedValue)) {
    theme.value["word-length"] = parsedValue.toString();
  }
}

async function openExample(contentKey: string) {
  await showExampleDialog({
    title: t("themePage.exampleDialogTitle"),
    content: t(`themePage.${contentKey}`),
  });
}

return { __sfc: true,props, t, theme, preTheme, unbindShortcutKeys, isMobileScreen, fieldLengthErrors, onResize, pageEditStore, themeStore, fetchTheme, updateTheme, instance, gtm, analytics, generateAnalytics, applyGeneratedTheme, isValidWordLength, changed, hasLengthError, canSave, onFieldValidationChange, submit, filterWordLengthDetails, formatWordLength, openExample, ThemeTextField, IconInfo }
}

})